import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useAppRedirect } from 'hooks/useAppRedirect';
import { useTicketInviteAccept } from 'services/datalayer/tickets';
import { mapPageEventToDatalayer } from 'services/datalayer/utils';
import { PageEventFragment } from 'types/api/dato-operations.types';

import DownloadApp from 'components/@account/DownloadApp';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import TicketsLine from 'components/@events/EventDetailCheckoutConfirmationContainer/TicketsLine';
import FormattedDateTime from 'components/@events/FormattedDateTime';
import Icon from 'components/@icons/Icon';

interface Props {
  event: PageEventFragment;
  hasMultipleEvents: boolean;
}

const TransferAcceptedScreen = ({ event, hasMultipleEvents }: Props) => {
  const intl = useIntl();
  const { onRedirect, isLoading } = useAppRedirect();

  useTicketInviteAccept({
    inviteSource: 'direct',
    ...mapPageEventToDatalayer(event),
  });

  const handleAppRedirect = () => {
    if (hasMultipleEvents) {
      onRedirect(Routes.AppTickets);
    } else {
      onRedirect(
        replacePathParams(Routes.AppTicketDetails, {
          slug: event.slug,
          eventId: event.id,
          ticketId: '0',
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        bgcolor: 'global01.800',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container
        sx={{
          mt: { xs: 2, md: 15 },
          mb: { xs: 3, md: 15 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { md: 'center' },
          flexGrow: 1,
        }}
      >
        <IconButton
          aria-label={intl.formatMessage({ id: 'navigation.close' })}
          href={Routes.Home}
          component={LinkUnstyled}
          sx={{
            position: 'absolute',
            right: { xs: 16, md: 24 },
            top: { xs: 16, md: 24 },
            zIndex: 1,
          }}
        >
          <Icon name="Close" />
        </IconButton>
        <Box
          sx={{
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: 'unset' },
            flexGrow: { xs: 1, md: 0 },
            gridTemplateColumns: { md: '7fr 5fr' },
            gap: { md: 2 },
          }}
        >
          <Stack
            sx={{
              flexGrow: 1,
              justifyContent: 'flex-end',
              alignItems: 'center',
              textAlign: 'center',
              mt: { xs: 4, md: 0 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: 'global01.400',
                textTransform: 'uppercase',
              }}
            >
              <FormattedMessage id="transfer_tickets.receive.accepted.title" />
            </Typography>
            {!hasMultipleEvents && (
              <>
                <Typography
                  variant="h7"
                  sx={{
                    color: 'global01.50',
                    mb: 0.5,
                    mt: { xs: 2, md: 5 },
                    wordBreak: 'normal',
                  }}
                >
                  {event.title}
                </Typography>
                <FormattedDateTime
                  endDatetime={event.endDatetime}
                  startDatetime={event.startDatetime}
                  timezone={event.timezone}
                />
              </>
            )}

            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                alignSelf: 'stretch',
                mt: 5,
                mb: 3,
              }}
            >
              <DownloadApp borderColor="global01.100" />
            </Box>
            <TicketsLine />

            <Button
              variant="outlined"
              color="secondary"
              sx={{
                mt: { xs: 3, md: 5 },
                width: { xs: '100%', md: 'fit-content' },
                display: { xs: 'none', md: 'flex' },
              }}
              component={LinkUnstyled}
              href={
                hasMultipleEvents
                  ? Routes.AccountTickets
                  : replacePathParams(Routes.AccountTicketsDetail, {
                      slug: event.slug,
                    })
              }
            >
              <FormattedMessage id="tickets.checkout.confirmation.view.tickets.button.multiple" />
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              sx={{
                mt: { xs: 3, md: 5 },
                width: { xs: '100%', md: 'fit-content' },
                display: { xs: 'flex', md: 'none' },
              }}
              loading={isLoading}
              onClick={handleAppRedirect}
            >
              <FormattedMessage id="tickets.checkout.confirmation.view.tickets_overview_mobile" />
            </Button>
          </Stack>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
            }}
          >
            <DownloadApp borderColor="global01.100" />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TransferAcceptedScreen;
